import { SecurityWithRank } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingTypes.ts';
import { GenericDrawer } from '@dealstack/frontend/shared/components/GenericDrawer/GenericDrawer.tsx';
import { Text } from '@mantine/core';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
	getSecurityDetails,
	getSecurityLiquidationPreference,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { getDrawerBody } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityFinancialTermsDrawer/financialTermsContentBuilders.tsx';
import { SecurityFeatureType } from '@dealstack/shared/lib/generated';

interface Props {
	opened: boolean;
	securityCard: SecurityWithRank | null;
	selectedEntityName: string;
	onClose: () => void;
}

export const SecurityFinancialTermsDrawer = ({
	opened,
	securityCard,
	selectedEntityName,
	onClose,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const tCommonUI = useTranslationCommonUi();

	const securityId = securityCard?.securityId;
	const getSecurityDetailsRequest = useQuery({
		queryKey: ['getSecurityDetails', securityId],
		queryFn: () => getSecurityDetails(securityId!),
		enabled: !!securityId,
	});

	// The LP terms live on a separate endpoint, fetched only for the liquidation-preference card.
	const showLiquidationPreference =
		!securityCard?.warrantOverId &&
		securityCard?.securityFeatureType === SecurityFeatureType.LIQUIDATION_PREFERENCE;

	const liquidationPreferenceRequest = useQuery({
		queryKey: ['getSecurityLiquidationPreference', securityId],
		queryFn: () => getSecurityLiquidationPreference(securityId!),
		enabled: !!securityId && showLiquidationPreference,
	});

	const drawerBody = useMemo(() => {
		if (!securityCard || !getSecurityDetailsRequest.data) {
			return null;
		}

		const featureType = securityCard?.warrantOverId
			? 'WARRANT'
			: securityCard.securityFeatureType;

		return getDrawerBody({
			tCapManage,
			tCommonUi: tCommonUI,
			security: getSecurityDetailsRequest.data,
			featureType,
			selectedEntityName,
			isMergedIncomeRight: !!securityCard?.isMergedIncomeRight,
			liquidationPreference: showLiquidationPreference
				? liquidationPreferenceRequest.data ?? null
				: null,
		});
	}, [
		securityCard,
		getSecurityDetailsRequest.data,
		liquidationPreferenceRequest.data,
		showLiquidationPreference,
		selectedEntityName,
	]);

	return (
		<GenericDrawer
			opened={opened}
			onClose={onClose}
			title={
				<Text fz="xl" fw={600} c="dark.8">
					{tCapManage('securityFinancialTerms.drawer.title')}
				</Text>
			}
			body={drawerBody}
		/>
	);
};
